import React, { useState, useEffect, memo } from "react";
import { DatePicker, Button, Spin, Skeleton, message, Space, Select, Segmented } from "antd";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import ReactWordcloud from "react-wordcloud";
import { ReloadOutlined, BarChartOutlined, CloudOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { getSemanticKeywords } from "../../api/api";
import type { SemanticKeywordsResponse } from "../../types/api-models";

const { RangePicker } = DatePicker;

type AggregationType = 'daily' | 'weekly' | 'monthly';
type ViewType = 'chart' | 'cloud';

interface CommonWordsAnalysisProps {
  isExpanded?: boolean;
  dashboardData?: any;
}

const CommonWordsAnalysis: React.FC<CommonWordsAnalysisProps> = ({ isExpanded = false, dashboardData }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<SemanticKeywordsResponse | null>(null);
  const [aggregation, setAggregation] = useState<AggregationType>('daily');
  const [viewType, setViewType] = useState<ViewType>('chart');
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(30, 'day'),
    dayjs()
  ]);
  const [wordLimit, setWordLimit] = useState(20);

  useEffect(() => {
    // For collapsed view with dashboard data, use it and skip fetching
    if (!isExpanded && dashboardData?.semantic_keywords) {
      // Normalize dashboard data (list) to match API response shape (object)
      const raw = dashboardData.semantic_keywords;
      const normalized = Array.isArray(raw) ? {
        top_words: raw,
        total_questions: 0,
        total_words: 0,
        date_range: { from: '', to: '' },
        aggregation_used: 'daily'
      } : raw;
      
      setData(normalized);
      setLoading(false);
      return;
    }
    
    // Skip fetch in collapsed view if no dashboard data yet (wait for it)
    if (!isExpanded) {
      return;
    }
    
    // Expanded mode - DON'T auto-load, reset to empty state
    setData(null);
    setLoading(false);
  }, [isExpanded, dashboardData]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let from_date: string;
      let to_date: string;
      
      // Calculate date range based on aggregation
      if (aggregation === 'daily') {
        if (!dateRange || !dateRange[0] || !dateRange[1]) {
          setLoading(false);
          return;
        }
        from_date = dateRange[0].format('YYYY-MM-DD');
        to_date = dateRange[1].format('YYYY-MM-DD');
      } else if (aggregation === 'weekly') {
        // Last 52 weeks
        const end = dayjs();
        const start = end.subtract(52, 'week');
        from_date = start.format('YYYY-MM-DD');
        to_date = end.format('YYYY-MM-DD');
      } else {
        // Monthly - last 12 months
        const end = dayjs();
        const start = end.subtract(12, 'month');
        from_date = start.format('YYYY-MM-DD');
        to_date = end.format('YYYY-MM-DD');
      }
      
      const response = await getSemanticKeywords(from_date, to_date, wordLimit);
      setData(response);
    } catch (error) {
      message.error('Failed to load common words');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  // Skeleton loader for collapsed view
  if (loading && !isExpanded) {
    return (
      <div style={{ padding: '16px' }}>
        <Skeleton active paragraph={{ rows: 3 }} />
      </div>
    );
  }

  // Show loading spinner
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Spin size="large" tip="Loading common words..." />
      </div>
    );
  }

  // Prepare chart data
  const hasData = data && data.top_words.length > 0;
  
  let emptyStateMessage;
  if (!hasData && isExpanded) {
    emptyStateMessage = (
      <div style={{ textAlign: 'center', padding: 60, color: '#666' }}>
        <p style={{ fontSize: 16, marginBottom: 8 }}>Ready to analyze common words</p>
        <p style={{ fontSize: 13, color: '#999' }}>Adjust filters above and click "Apply Filters" to view data</p>
      </div>
    );
  } else if (!hasData) {
    emptyStateMessage = (
      <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
        No word data available
      </div>
    );
  }
  
  const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#8dd1e1', '#a4de6c', '#d084d0', '#8a89a6', '#ffbb28', '#ff6b6b'];
  
  let contentComponent;
  
  if (!hasData) {
    // Show empty state but DON'T early return (so controls still show)
    contentComponent = emptyStateMessage;
  } else {
    if (viewType === 'chart') {
      const displayWords = isExpanded ? data.top_words : data.top_words.slice(0, 5);
      const chartData = displayWords.map((item, index) => ({
        word: item.word,
        count: Number(item.count), // Ensure number type
        fill: colors[index % colors.length],
      }));

      contentComponent = (
        <div style={{ height: isExpanded ? 350 : 200, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart 
              data={chartData} 
              layout="vertical"
              margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
            >
              <XAxis type="number" />
              <YAxis 
                type="category" 
                dataKey="word" 
                width={90}
                tick={{ fontSize: 12 }}
              />
              <Tooltip 
                formatter={(value: number) => [value, 'Count']}
                contentStyle={{ fontSize: 12 }}
              />
              <Bar dataKey="count" isAnimationActive={false}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      );
    } else {
      // Word Cloud View
      const words = data.top_words.map(item => ({
        text: item.word,
        value: item.count,
      }));

      const options = {
        rotations: 2,
        rotationAngles: [0, 90] as [number, number],
        fontSizes: [14, 64] as [number, number], // Slightly smaller max font to fit better
        padding: 2,
        enableTooltip: true,
        deterministic: true,
        fontFamily: 'Arial, sans-serif',
        fontWeight: 'normal' as 'normal',
        scale: 'sqrt' as 'sqrt',
      };

      contentComponent = (
        <div style={{ height: isExpanded ? 350 : 240, width: '100%' }}>
          <ReactWordcloud 
            words={words} 
            options={options}
          />
        </div>
      );
    }
  }

  // If not expanded, return content only (defaulting to Chart view usually, or simple list)
  if (!isExpanded) {
    return contentComponent;
  }

  // Expanded view with controls
  return (
    <div>
      {/* Controls Row */}
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <Space wrap>
          <span style={{ fontSize: 13, color: '#666' }}>View:</span>
          <Segmented
            value={viewType}
            onChange={(value) => setViewType(value as ViewType)}
            options={[
              { label: 'Chart', value: 'chart', icon: <BarChartOutlined /> },
              { label: 'Word Cloud', value: 'cloud', icon: <CloudOutlined /> }
            ]}
          />
          
          <span style={{ fontSize: 13, color: '#666', marginLeft: 16 }}>Aggregation:</span>
          <Segmented
            value={aggregation}
            onChange={(value) => setAggregation(value as AggregationType)}
            options={[
              { label: 'Daily', value: 'daily' },
              { label: 'Weekly', value: 'weekly' },
              { label: 'Monthly', value: 'monthly' }
            ]}
          />
          {aggregation === 'daily' ? (
            <>
              <span style={{ fontSize: 13, color: '#666', marginLeft: 16 }}>Range:</span>
              <RangePicker
                value={dateRange}
                onChange={(dates) => {
                  if (dates && dates[0] && dates[1]) {
                    setDateRange([dates[0], dates[1]]);
                  }
                }}
                format="YYYY-MM-DD"
                style={{ width: 240 }}
                disabledDate={(current) => {
                  if (!current) return false;
                  return current.isAfter(dayjs().endOf('day'));
                }}
              />
            </>
          ) : (
            <span style={{ fontSize: 13, color: '#666', marginLeft: 16, fontStyle: 'italic' }}>
              Last {aggregation === 'weekly' ? '52 weeks' : '12 months'}
            </span>
          )}
          
          <Select
            value={wordLimit}
            onChange={setWordLimit}
            style={{ width: 100 }}
            options={[
              { label: 'Top 10', value: 10 },
              { label: 'Top 20', value: 20 },
              { label: 'Top 50', value: 50 },
              { label: 'Top 100', value: 100 },
            ]}
          />
        </Space>
        
        <Space>
          <Button type="primary" onClick={fetchData} loading={loading}>
            Apply
          </Button>
          <Button icon={<ReloadOutlined />} onClick={fetchData} disabled={!hasData}>
            Refresh
          </Button>
        </Space>
      </Space>

      {/* Content */}
      {contentComponent}
      
      {/* Info footer */}
      {hasData && data && (
        <div style={{ marginTop: 16, fontSize: 12, color: '#999', textAlign: 'center' }}>
          Analyzed {data.total_questions.toLocaleString()} questions ({data.total_words.toLocaleString()} words) • 
          Aggregation: {data.aggregation_used} • 
          Date range: {dayjs(data.date_range.from).format('MMM D, YYYY')} - {dayjs(data.date_range.to).format('MMM D, YYYY')}
        </div>
      )}
    </div>
  );
};

export default memo(CommonWordsAnalysis);
